"use client";

import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import useModalScrollLock from "@/hooks/useModalScrollLock";
import EnqueryForm from "@/components/forms/EnqueryForm";
import { FormHeading, SuccessMessageType } from "@/interface/types";

interface AiLabsProps {
  /** Routes the lead to a course. Defaults to the home page's own audience. */
  pageName?: string;
}

const BASE = "https://ik.imagekit.io/mileseducation/miles_website/home_2026";
const bgSrc = (file: string, width: number) =>
  `${BASE}/${file}?tr=f-webp,q-55,w-${width}`;

// Source is 3024x694. Ladder stops there — past it ImageKit only upscales.
const DESKTOP_BG_WIDTHS = [1024, 1280, 1600, 1920, 2400, 3024];
// Source is 786x1106.
const MOBILE_BG_WIDTHS = [400, 560, 786];

const srcSetFor = (file: string, widths: number[]) =>
  widths.map((width) => `${bgSrc(file, width)} ${width}w`).join(", ");

const artSrc = (file: string, updatedAt?: number) =>
  `${BASE}/${file}${updatedAt ? `?updatedAt=${updatedAt}` : ""}`;

const DESKTOP_ART_UPDATED_AT = 1785992031268;

const ART_ALT =
  "Microsoft 365 Copilot, Excel, Word, PowerPoint, Power BI and Power Automate wired into an AI core";

const CAIRA_LOGO_SRC =
  "https://ik.imagekit.io/mileseducation/miles_website/caira/caira_sm_logo.webp";

const gradientText = (background: string): React.CSSProperties => ({
  background,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
});

/** Green through teal and yellow to pink across "Copilot Studio". */
const COPILOT_GRADIENT =
  "linear-gradient(90deg, #5AD026 0%, #1ED7C7 50%, #F1D518 69.23%, #F358B4 100%)";

const CloseGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    className="h-3.5 w-3.5"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const CAIRA_COURSE_ID = 18;

/** Each reads "NO <label>" — the word NO is the shared, oversized half. */
const features = [
  { id: "no-code", label: "Code" },
  { id: "no-client-data", label: "Client Data" },
  { id: "no-set-up", label: "Set-up" },
];

const ExpertFormModal = ({
  pageName,
  courseId,
  onClose,
}: {
  pageName: string;
  courseId: number;
  onClose: () => void;
}) => {
  const [mounted, setMounted] = useState(false);

  useModalScrollLock(true, {
    restoreScrollPosition: true,
    preventTouchScroll: true,
  });

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Talk to an expert"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/80 p-4"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="relative w-full max-w-[400px] rounded-2xl bg-[#0B0B0B] p-5"
        style={{ boxShadow: "0px 0px 14px 0px rgba(4, 84, 175, 0.29)" }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="no-min-size absolute right-3 top-3 z-10 flex h-6 w-6 min-w-[24px] items-center justify-center rounded-full border border-[#39F] text-[#39F]"
        >
          <CloseGlyph />
        </button>

        <EnqueryForm
          course="CAIRA"
          courseId={courseId}
          pageName={pageName}
          darkBackground
          formType="page"
          formHeading={FormHeading.TALK_TO_AN_EXPERT}
          submitButtonName="Request a Callback"
          successMessage={SuccessMessageType.ACCOUNTING}
        />
      </div>
    </div>,
    document.body,
  );
};

const AiLabs: React.FC<AiLabsProps> = ({ pageName = "CPA/CMA" }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const closeForm = useCallback(() => setIsFormOpen(false), []);

  return (
    <section className="relative w-full overflow-hidden bg-[#04070F]">
      <picture>
        <source
          media="(min-width: 1024px)"
          srcSet={srcSetFor("desktop_bg_updated.webp", DESKTOP_BG_WIDTHS)}
          sizes="100vw"
        />
        <img
          src={bgSrc("mobile_bg_updated.webp", 786)}
          srcSet={srcSetFor("mobile_bg_updated.webp", MOBILE_BG_WIDTHS)}
          sizes="100vw"
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </picture>

      <div className="relative z-10 grid grid-cols-1 items-center px-4 pt-7 text-left lg:grid-cols-[minmax(0,1fr)_minmax(0,46%)] lg:gap-x-8 lg:px-8 lg:py-10 xl:px-[120px] min-[1700px]:pl-[170px]">
        {/* Copy */}
        <div>
          <p className="m-0 mb-3 inline-block rounded-[8px] border border-white px-[10px] py-[5px] text-[14px] font-semibold uppercase leading-tight tracking-[0.02em] text-white md:mb-[10px] md:text-base">
            World&rsquo;s First
          </p>

          <h2 className="m-0 text-[36px] font-extrabold leading-[1] text-white md:text-[47px]">
            <span className="text-[#FFBA08]">AI</span> Labs for
            <br />
            Accountants
          </h2>

          <p className="mt-3 inline-block bg-[#FFBA08] px-4 py-2 text-[18px] font-bold leading-tight text-[#1D1D1D] md:mt-2 md:text-[22px] lg:px-3.5 lg:py-1.5">
            Build AI Agents &amp; Workflows
          </p>

          <p className="mt-4 md:mt-6 text-[18px] font-medium leading-snug text-white md:text-[22px]">
            12 months complimentary
            <br className="md:hidden block" /> access to{" "}
            <span
              className="inline-block font-bold"
              style={gradientText(COPILOT_GRADIENT)}
            >
              Copilot Studio
            </span>
          </p>
          <p className="mt-3 inline-flex items-center gap-2 rounded-[8px] border border-white px-[10px] py-[5px] text-[18px] font-bold leading-tight text-white md:mt-4 md:text-[22px]">
            FREE with
            <img
              src={CAIRA_LOGO_SRC}
              alt="CAIRA"
              width={297}
              height={74}
              loading="lazy"
              decoding="async"
              className="h-[18px] w-auto md:h-[20px]"
            />
          </p>

          {/* items-stretch so the dividers run the full height of the pair. */}
          <div className="mt-3 flex items-stretch md:mt-4">
            {features.map((feature, index) => (
              <div
                key={feature.id}
                className={`flex flex-col justify-center px-4 md:justify-start lg:px-5 ${
                  index === 0
                    ? "items-start pl-0 lg:pl-0"
                    : "items-center border-l border-[#FFBA08] lg:border-[#FFBA08]"
                }`}
              >
                <span className="text-[24px] font-bold leading-none text-white md:text-[36px]">
                  NO
                </span>
                <span className="mt-1 whitespace-nowrap text-[16px] leading-snug text-white md:text-[22px]">
                  {feature.label}
                </span>
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setIsFormOpen(true)}
            className="gcc-border relative mt-5 rounded-[20px_20px_20px_0] bg-white px-9 font-bold text-black md:mt-6 lg:px-14 py-2 text-[14px]"
          >
            Know More
          </button>
        </div>

        <picture className="flex justify-end lg:justify-center">
          <source
            media="(min-width: 1024px)"
            srcSet={artSrc("ai_labs_updated.png", DESKTOP_ART_UPDATED_AT)}
          />
          <img
            src={artSrc("ai_labs_mobile.png")}
            alt={ART_ALT}
            loading="lazy"
            decoding="async"
            className="aspect-square h-auto w-[226px] max-w-[300px] object-contain lg:aspect-[1346/680] lg:w-full lg:max-w-none relative md:top-0 top-[-20px]"
          />
        </picture>
      </div>

      {isFormOpen && (
        <ExpertFormModal
          pageName={pageName}
          courseId={CAIRA_COURSE_ID}
          onClose={closeForm}
        />
      )}
    </section>
  );
};

export default AiLabs;
